import { useContext } from "react";
import { Stack, IconButton, Link, Chip, Tooltip } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import DeleteIcon from "@mui/icons-material/Delete";

import withRole from "../../common/roles/withRole";
import { Role } from "../../common/roles/role";
import useModal from "../../common/hooks/useModal";
import { getFormattedStatus } from "../../helpers";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import ShowIcon from "@mui/icons-material/Visibility";
import { formatDate } from "../../helpers";
import { TargetTabContext } from "../../views/notifications";

export const NotificationsTable = ({
  rowsState,
  setRowsState,
  actionToCancel,
}) => {
  const { targetTab, distributors, pointsOfSale } =
    useContext(TargetTabContext);

  const history = useHistory();

  const { role } = useSelector((state) => state.auth.user);
  const RestrictedMarinaAction = withRole([Role.Marina])(IconButton);

  const { pageSize, total, rows } = rowsState;
  const modal = useModal();

  const getNotificationsStatus = (cellValues) => {
    const status = cellValues.row.status;
    const start_date = cellValues.row.start_date;
    const end_date = cellValues.row.end_date;
    let label = "";

    switch (true) {
      case start_date < end_date &&
        new Date() > end_date &&
        new Date() > start_date:
        label = getFormattedStatus("CADUCADA");
        break;
      case start_date > end_date:
        label = getFormattedStatus("CANCELADA");
        break;
      case start_date < end_date &&
        new Date() < start_date &&
        new Date() < end_date:
        label = getFormattedStatus("PROGRAMADA");
        break;
      default:
        label = getFormattedStatus(status);
        break;
    }

    return label;
  };

  const columns = [
    {
      headerName: "Título",
      title: "Title",
      field: "title",
      minWidth: 250,
      renderCell: (cellValues) => {
        return (
          <Link
            href={
              targetTab === "distributor"
                ? `/notification/${cellValues.row.id}`
                : `/notification/${cellValues.row.id}?type=pdv`
            }
            underline="hover"
            color="inherit"
          >
            {cellValues.row.title}
          </Link>
        );
      },
    },
    targetTab === "distributor" && {
      headerName: "Destinatario",
      title: "Target",
      field: "target",
      minWidth: 150,
      align: "left",
      renderCell: (cellValues) => {
        const distributor = distributors.find(
          (d) => d.id === cellValues.row.target
        );
        return (
          <span>{distributor ? distributor.name : cellValues.row.target}</span>
        );
      },
    },
    targetTab === "point_of_sale" && {
      headerName: "Punto de venta",
      title: "Point of Sale",
      field: "point_of_sale",
      minWidth: 150,
      align: "left",
      renderCell: (cellValues) => {
        const pointOfSale = pointsOfSale.find(
          (p) => p.id === cellValues.row.target
        );
        return (
          <span>{pointOfSale ? pointOfSale.name : cellValues.row.target}</span>
        );
      },
    },
    {
      headerName: "Fecha Activación",
      title: "Start Date",
      field: "start_date",
      minWidth: 150,
      align: "left",
      renderCell: (cellValues) =>
        formatDate(cellValues.row.start_date, "es-ES", {
          year: "numeric",
          month: "numeric",
          day: "numeric",
          hour: "numeric",
          minute: "numeric",
        }),
    },
    {
      headerName: "Fecha Fin",
      title: "End Date",
      field: "end_date",
      minWidth: 150,
      align: "left",
      renderCell: (cellValues) =>
        formatDate(cellValues.row.end_date, "es-ES", {
          year: "numeric",
          month: "numeric",
          day: "numeric",
          hour: "numeric",
          minute: "numeric",
        }),
    },
    {
      headerName: "Creador",
      title: "User",
      field: "create_user",
      minWidth: 150,
      flex: 1,
      align: "left",
    },
    {
      headerName: "Estado",
      title: "status",
      field: "status",
      minWidth: 100,
      align: "center",
      renderCell: (cellValues) => {
        let label = getNotificationsStatus(cellValues);
        return (
          <Chip
            variant="outlined"
            label={label.text}
            size="small"
            color={label.color}
          />
        );
      },
    },
  ];

  if (role === Role.Marina) {
    columns.push({
      headerName: "Acciones",
      field: "actions",
      type: "actions",
      width: 100,
      align: "center",
      renderCell: (cellValues) => {
        return (
          <>
            <IconButton
              aria-label="Show notification"
              edge="start"
              size="small"
              onClick={(evt) => {
                targetTab === "distributor"
                  ? history.push(`/notification/${cellValues.id}`)
                  : history.push(`/notification/${cellValues.id}?type=pdv`)
              }}            
            >
              <Tooltip title="Ver">
                <ShowIcon />
              </Tooltip>
            </IconButton>

            {getNotificationsStatus(cellValues).text === "Programada" && targetTab === "distributor" ? (
              <RestrictedMarinaAction
                aria-label="Cancel notification"
                size="small"
                onClick={(evt) =>
                  modal.show({
                    title: "Cancel",
                    content: `¿Estás seguro que quieres cancelar esta notificación: ${cellValues.row.title}?`,
                    onSubmit: () => {
                      actionToCancel(cellValues);
                    },
                  })
                }
              >
                <Tooltip title="Cancelar">
                  <DeleteIcon />
                </Tooltip>
              </RestrictedMarinaAction>
            ) : null}
          </>
        );
      },
    });
  }

  return (
    <div style={{ height: 430, width: "100%", marginBottom: 30 }}>
      <DataGrid
        rows={rows}
        columns={columns}
        rowCount={total}
        {...rowsState}
        pageSize={pageSize}
        rowsPerPageOptions={[pageSize]}
        pagination
        paginationMode="server"
        onPageSizeChange={(pageSize) =>
          setRowsState((prev) => ({ ...prev, reload: true, pageSize }))
        }
        onPageChange={(page) => {
          setRowsState((prev) => ({ ...prev, reload: true, page }));
        }}
        components={{
          NoRowsOverlay: () => (
            <Stack height="100%" alignItems="center" justifyContent="center">
              No hay resultados
            </Stack>
          ),
        }}
      />
    </div>
  );
};
